// -----------------------------------------------------------------------------
// FanCamIA.jsx
// Formulario para subir una o varias imágenes desde el Admin.
// - Previsualiza archivos
// - Valida tipo/tamaño/cantidad
// - Sube vía multipart/form-data
// - Incluye metadata { source: "selfie_ia", caption, tags }
//
// Requisitos:
//  - BLUploadSelfie debe aceptar { mode: "multipart", formData }  O
//    implementa BLUploadImageFormData(formData) si prefieres otra firma.
//  - useFloatMessage/MESSAGE_TYPE son opcionales; usa tu propio sistema de toasts.
// -----------------------------------------------------------------------------

import { useRef, useState } from 'react'
import { UploadMultipleImages as BLUploadMultipleImages } from './../../../utilities/BL/SelfieFan'
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'

const DEFAULT_ACCEPT = "image/*"

const bytesToMB = (bytes) => +(bytes / (1024 * 1024)).toFixed(2);

/**
 * FanCamIA
 *
 * Props opcionales para parametrizar límites:
 * - accept: tipos MIME aceptados (por defecto "image/*")
 * - maxFiles: cantidad máxima de archivos (por defecto 10)
 * - maxTotalMB: peso total máximo por lote (por defecto 50 MB)
 * - maxEachMB: peso máximo por archivo (por defecto 10 MB)
 */
const FanCamIA = ({ accept = DEFAULT_ACCEPT, maxFiles = 10, maxTotalMB = 50, maxEachMB = 10 }) => {
  const { showMessage } = useFloatMessage()

  // --- Estado del formulario ---
  const [files, setFiles] = useState([])         // File[]
  const [caption, setCaption] = useState("")     // Texto corto opcional
  const [tags, setTags] = useState("")           // "tag1, tag2"
  const [isUploading, setIsUploading] = useState(false)
  const [errorText, setErrorText] = useState("")
  const [successText, setSuccessText] = useState("")

  // Input ref para poder limpiarlo luego
  const fileInputRef = useRef(null)

  // --- Validaciones ---
  const validateFiles = (list) => {
    if (list.length === 0) return "Selecciona al menos una imagen."
    if (list.length > maxFiles) return `Máximo ${maxFiles} archivos.`

    let totalBytes = 0
    for (const f of list) {
      if (!f.type.startsWith("image/")) {
        return `El archivo "${f.name}" no es una imagen válida.`
      }
      totalBytes += f.size
      if (bytesToMB(f.size) > maxEachMB) {
        return `El archivo "${f.name}" excede ${maxEachMB} MB.`
      }
    }
    if (bytesToMB(totalBytes) > maxTotalMB) {
      return `El tamaño total excede ${maxTotalMB} MB.`
    }
    return ""
  }

  // --- Handlers ---
  const handlePickFiles = (e) => {
    //setSuccessText("")
    //setErrorText("")
    const picked = Array.from(e.target.files || [])
    const msg = validateFiles(picked)
    if (msg) {
      setFiles([])
      //setErrorText(msg)
      showMessage?.({ type: MESSAGE_TYPE.BAD, text: msg })
      return
    }
    setFiles(picked)
  }

  const clearSelection = () => {
    setFiles([])
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    //setSuccessText("")
    //setErrorText("")

    if (files.length === 0) {
        const msg = "Selecciona al menos una imagen."
        //setErrorText(msg)
        showMessage?.({ type: MESSAGE_TYPE.BAD, text: msg })
        return
    }

    const msg = validateFiles(files)
    if (msg) {
        //setErrorText(msg)
        showMessage?.({ type: MESSAGE_TYPE.BAD, text: msg })
        return
    }

    try {
        setIsUploading(true)

        //   // Construye FormData (multipart)
        //   const formData = new FormData()
        //   files.forEach((file) => formData.append("files", file, file.name))

        //   // Metadata para diferenciar en Admin
        //   formData.append("source", "selfie_ia")  // <<<<<<<<<<<<<<<<<<<<<<<< METADATA
        //   formData.append("caption", caption)
        //   formData.append("tags", tags) // tu backend puede splittear por coma

        //   // Llamada a BL (modo multipart). Ajusta según tu implementación real:
        //   await BLUploadSelfie({ mode: "multipart", formData })

        //   setSuccessText("¡Imágenes subidas correctamente!")
        //   showMessage?.({ type: MESSAGE_TYPE.SUCCESS, text: "¡Imágenes subidas correctamente!" })

        //   // Limpiar formulario
        //   clearSelection()
        //   setCaption("")
        //   setTags("")
        
        const { success, results } = await BLUploadMultipleImages({
            files,
            caption,
            source: "selfie_ia",    // 👈 clave para filtrar en Admin
        })
        
        if (!success) {
            // Puedes construir un msg con fallas:
            const fallas = results.filter(r => !r.success).map(r => r.name).join(", ")
            throw new Error(`Algunas imágenes fallaron: ${fallas}`)
        }
        
        //setSuccessText("¡Imágenes subidas correctamente!")
        showMessage?.({ type: MESSAGE_TYPE.SUCCESS, text: "¡Imágenes subidas correctamente!" })
        clearSelection()
        setCaption("")
        setTags("") // si no la usas, puedes quitarla en esta versión binaria
    } catch (err) {
        console.error(err)
        const msg = err?.message || "Error al subir imágenes. Inténtalo de nuevo."
        //setErrorText(msg)
        showMessage?.({ type: MESSAGE_TYPE.BAD, text: msg })
    } finally {
        setIsUploading(false)
    }
  }


  return (
    <div className="FanCamIA container">
      <img src="/img/page/xolos-five-bicolor.png" className="LogoADSLxZtudia" />
      {/* <p className="FanCamIA__Title">Subir imágenes con IA</p> */}

      <p className='text-center fs-3 mt-3 mb-2'>Generador de FanCam IA</p>
      <form className="FanCamIA__Form" onSubmit={handleSubmit}>
        {/* Selector de archivos */}
        {/* <div className="FanCamIA__Row">
          <p className="FanCamIA__p">Selecciona las imágenes</p>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple
            onChange={handlePickFiles}
          />
          <small className="FanCamIA__Help">
            Tipos: {accept}. Máx archivos: {maxFiles}. Máx cada archivo: {maxEachMB} MB.
            Máx total: {maxTotalMB} MB.
          </small>
        </div> */}

        <p className="FanCamIA__Title">Selecciona las imágenes con IA que deseas subir:</p>
        <input className="FanCamIA__Form__SelectButton" ref={fileInputRef} type="file" accept={accept} multiple onChange={handlePickFiles}/>
        <p className="FanCamIA__Form__Help">Tipos: {accept}. Máx archivos: {maxFiles}. Máx cada archivo: {maxEachMB} MB. Máx total: {maxTotalMB} MB.</p>
        
        {/* Vista previa */}
        {files.length > 0 && (
          <div className="FanCamIA__Form__Preview">
            <p className='FanCamIA__Title'>Vista previa</p>
            <p className='FanCamIA__Subtitle'>Total de imágenes a subir: {files.length}</p>

            {files.map((file, i) => {
              const url = URL.createObjectURL(file)

              return (
                <div key={`${file.name}-${i}`} className="FanCamIA__Form__Preview__Thumb">
                  <img className='FanCamIA__Form__Preview__Thumb__Image' src={url} alt={file.name} onLoad={() => URL.revokeObjectURL(url)}/>
                  {/* <p className="FanCamIA__Form__Preview__Thumb__Text" title={file.name}>Nombre: {file.name}</p> */}
                  <p className="FanCamIA__Form__Preview__Thumb__Text">Tamaño: {bytesToMB(file.size)} MB</p>
                </div>
              )
            })}
          </div>
        )}

        {/* Metadata opcional */}
        {/* <div className="FanCamIA__Row">
          <label className="FanCamIA__Label">Caption</label>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Descripción corta opcional"
          />
        </div>

        <div className="FanCamIA__Row">
          <label className="FanCamIA__Label">Tags</label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Ej. admin, galeria, final"
          />
          <small className="FanCamIA__Help">Separadas por coma</small>
        </div> */}

        <div className="FanCamIA__Form__Actions">
          <button type="submit" className="Btn__Adsl--Red FanCamIA__Form__Actions__Buttons" disabled={isUploading || files.length === 0}>{isUploading ? "Subiendo..." : `${files.length === 0 ? 'Subir ahora' : files.length === 1 ? 'Subir imagen ahora' : `Subir ${files.length} imágenes ahora`}`}</button>
          <button type="button" className="Btn__Adsl--RedOutline FanCamIA__Form__Actions__Buttons" onClick={clearSelection} disabled={isUploading || files.length === 0}>Limpiar fotos a subir</button>
        </div>

        {/* Estados y acciones */}
        {/* {errorText && <p className="FanCamIA__Error"><strong>Error:</strong> {errorText}</p>} */}
        {/* {successText && <p className="FanCamIA__Success">{successText}</p>} */}
      </form>

      <p className='text-center mt-5 mb-3 FooterCredits'>Hecho con ♥ por Interaktia | 2026</p>
    </div>
  )
}

export { FanCamIA };